import { useMemo, useState } from 'react';
import {
  CheckCircle2,
  Cloud,
  Loader2,
  Rocket,
  Shield,
  XCircle,
} from 'lucide-react';
import {
  bootstrapApi,
  formatBootstrapErrorMessage,
  getBootstrapNextActionHint,
  getBootstrapPhaseLabel,
  getPlatformApiBase,
  toBootstrapErrorMeta,
} from '../utils/api';
import {
  normalizeControlApiBase,
  readBootstrapConfig,
  resetPlatformProvisionForReimport,
  saveBootstrapConfig,
} from '../utils/bootstrapConfig';
import { verifyCloudflareTokenAndAccount } from '../utils/cloudflareBootstrap';
import { verifyUserControlEndpoint } from '../utils/userCloudflareApi';

const STAGE_LABELS = {
  discover: '自动发现 Control API',
  probe: 'Permission and resource checks',
  finalize: '写入启动配置',
};

const DEFAULT_STAGE_STATE = {
  discover: { status: 'idle', detail: '' },
  probe: { status: 'idle', detail: '' },
  finalize: { status: 'idle', detail: '' },
};

const BOOTSTRAP_TERMINAL_STATES = new Set(['done', 'action_required', 'failed']);
const RETRY_PHASE_OPTIONS = [
  { value: '', label: '当前失败阶段' },
  { value: 'ensure_resources', label: 'ensure_resources' },
  { value: 'ensure_worker', label: 'ensure_worker' },
  { value: 'ensure_route', label: 'ensure_route' },
  { value: 'wait_dns_propagation', label: 'wait_dns_propagation' },
  { value: 'verify_domain', label: 'verify_domain' },
];

function wait(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function isNetworkFailureMessage(message = '') {
  const text = String(message || '').toLowerCase();
  return text.includes('failed to fetch') || text.includes('networkerror');
}

function isSafeControlDomainCandidate(domain = '') {
  const normalized = String(domain || '').trim().toLowerCase();
  if (!normalized) return false;
  if (normalized.includes('*')) return false;
  return normalized.startsWith('api.') && normalized.includes('.');
}

function getRebindStatusLabel(value = '') {
  const normalized = String(value || '').trim().toLowerCase();
  if (!normalized) return 'unknown';
  if (normalized === 'deployed') return 'deployed';
  if (normalized === 'not_deployed') return 'not deployed';
  return normalized;
}

function getRebindStatusClass(value = '') {
  const normalized = String(value || '').trim().toLowerCase();
  if (normalized === 'ok' || normalized === 'deployed') {
    return 'bg-emerald-500/10 text-emerald-200 border border-emerald-300/30';
  }
  if (normalized === 'pending' || normalized === 'wait') {
    return 'bg-amber-500/10 text-amber-200 border border-amber-300/30';
  }
  return 'bg-red-500/10 text-red-200 border border-red-300/30';
}

function toScopeList(value) {
  if (!Array.isArray(value)) return [];
  return value.map((item) => String(item || '').trim()).filter(Boolean);
}

function noviceControlApiBaseTips(probe) {
  const missing = Array.isArray(probe?.missing) ? probe.missing : [];
  if (!missing.includes('control_api_base')) return [];
  return [
    'Paste your Cloudflare API token above. In most cases, Control API can stay empty.',
    'During first-time bootstrap, Mirror-X sends this token once to provision cloud resources. If it fails, contact support or ops.',
  ];
}

function deriveRecoveryTips(probe) {
  const prependNovice = (tips) => {
    const prefix = noviceControlApiBaseTips(probe);
    return prefix.length ? [...prefix, ...tips] : tips;
  };

  const actionTips = Array.isArray(probe?.actions)
    ? probe.actions
        .map((item) => {
          const title = String(item?.title || '').trim();
          const description = String(item?.description || '').trim();
          if (!title && !description) return '';
          return description ? `${title}: ${description}` : title;
        })
        .filter(Boolean)
    : [];

  if (actionTips.length > 0) {
    return prependNovice(actionTips);
  }

  const checks = Array.isArray(probe?.checks) ? probe.checks : [];
  const tokenCheck = checks.find((item) => item.key === 'token_verify');
  const allNetworkFailed =
    checks.length > 0 && checks.every((item) => isNetworkFailureMessage(item?.message || ''));

  if (allNetworkFailed) {
    return prependNovice([
      'No reachable user-side Control Worker detected. Deploy the user worker first, then retry.',
      'If deployed already, ensure ALLOWED_ORIGINS includes the current admin domain.',
    ]);
  }

  if (tokenCheck && !tokenCheck.ok && !isNetworkFailureMessage(tokenCheck.message || '')) {
    return prependNovice([
      'Token verification failed. Ensure token includes Workers Scripts, Workers KV Storage, and D1 permissions.',
    ]);
  }

  return prependNovice(['Probe failed. Complete missing capabilities and retry auto-detection.']);
}

function collectActionDocs(probe) {
  const actions = Array.isArray(probe?.actions) ? probe.actions : [];
  const seenUrls = new Set();
  const output = [];

  for (const action of actions) {
    const docs = Array.isArray(action?.docs) ? action.docs : [];
    const actionCode = String(action?.code || '').trim();
    const actionTitle = String(action?.title || '').trim();
    for (const doc of docs) {
      const url = String(doc?.url || '').trim();
      if (!url || seenUrls.has(url)) continue;
      seenUrls.add(url);
      output.push({
        url,
        label: String(doc?.label || doc?.title || url).trim(),
        actionCode,
        actionTitle,
      });
    }
  }

  return output;
}

function getInitialOnboardingStep(config) {
  if (
    config?.provisionCompleted &&
    !String(config?.cloudflareApiToken || '').trim()
  ) {
    return 3;
  }
  return 1;
}

function BootstrapOnboarding({ initialConfig = null, onComplete }) {
  const [step, setStep] = useState(() => getInitialOnboardingStep(initialConfig));
  const [token, setToken] = useState(() =>
    getInitialOnboardingStep(initialConfig) === 3 ? '' : initialConfig?.cloudflareApiToken || ''
  );
  const [manualControlApiBase, setManualControlApiBase] = useState(
    initialConfig?.controlApiBase || ''
  );
  const [localToken, setLocalToken] = useState('');
  const [localTokenError, setLocalTokenError] = useState('');
  const [isSavingLocalToken, setIsSavingLocalToken] = useState(false);
  const [tokenError, setTokenError] = useState('');
  const [isVerifyingToken, setIsVerifyingToken] = useState(false);

  const [verification, setVerification] = useState(
    initialConfig?.accountId
      ? {
          ok: true,
          accountId: initialConfig.accountId,
          accountName: initialConfig.accountName || '',
          tokenStatus: initialConfig.tokenStatus || 'active',
          verifiedAt: initialConfig.tokenVerifiedAt || '',
        }
      : null
  );

  const [controlApiError, setControlApiError] = useState('');
  const [isVerifyingControlApi, setIsVerifyingControlApi] = useState(false);
  const [isRetryingTask, setIsRetryingTask] = useState(false);
  const [isRebindingDomain, setIsRebindingDomain] = useState(false);
  const [retryFromPhase, setRetryFromPhase] = useState('');
  const [controlApiProbe, setControlApiProbe] = useState(null);
  const [provisionStages, setProvisionStages] = useState(DEFAULT_STAGE_STATE);
  const [shouldDeployTemplate, setShouldDeployTemplate] = useState(
    initialConfig?.shouldDeployTemplate ?? true
  );
  const persistedConfig = readBootstrapConfig() || {};
  const persistedDowngradeRecommendation =
    persistedConfig?.downgradeRecommendation &&
    typeof persistedConfig.downgradeRecommendation === 'object'
      ? persistedConfig.downgradeRecommendation
      : null;
  const runtimeScopeHint = Array.isArray(persistedDowngradeRecommendation?.recommendedScopes)
    ? persistedDowngradeRecommendation.recommendedScopes.join(', ')
    : '';
  const runtimeDeadlineHint = Number(persistedDowngradeRecommendation?.deadlineHours || 0);

  const maskedToken = useMemo(() => {
    if (!token) return '';
    if (token.length <= 8) return '*'.repeat(token.length);
    return `${token.slice(0, 4)}${'*'.repeat(token.length - 8)}${token.slice(-4)}`;
  }, [token]);

  const recoveryTips = useMemo(() => deriveRecoveryTips(controlApiProbe), [controlApiProbe]);
  const actionDocs = useMemo(() => collectActionDocs(controlApiProbe), [controlApiProbe]);
  const bootstrapFailureMeta = useMemo(() => toBootstrapErrorMeta(controlApiProbe || {}), [controlApiProbe]);
  const permissionAudit = useMemo(() => {
    const raw =
      controlApiProbe?.permissionAudit && typeof controlApiProbe.permissionAudit === 'object'
        ? controlApiProbe.permissionAudit
        : null;
    if (!raw) return null;
    return {
      profile: String(raw.profile || '').trim(),
      canProceed: Boolean(raw.canProceed),
      riskLevel: String(raw.riskLevel || '').trim(),
      message: String(raw.message || '').trim(),
      requiredScopes: toScopeList(raw.requiredScopes),
      grantedScopes: toScopeList(raw.grantedScopes),
      missingScopes: toScopeList(raw.missingScopes),
    };
  }, [controlApiProbe]);
  const onboardingDowngradeRecommendation = useMemo(() => {
    const raw =
      controlApiProbe?.downgradeRecommendation &&
      typeof controlApiProbe.downgradeRecommendation === 'object'
        ? controlApiProbe.downgradeRecommendation
        : null;
    if (!raw) return null;
    return {
      shouldRotate: Boolean(raw.shouldRotate),
      targetProfile: String(raw.targetProfile || '').trim(),
      deadlineHours: Number(raw.deadlineHours || 0),
      message: String(raw.message || '').trim(),
      recommendedScopes: toScopeList(raw.recommendedScopes),
    };
  }, [controlApiProbe]);
  const rebindResult = useMemo(() => {
    const raw = controlApiProbe?.lastRebindResult;
    return raw && typeof raw === 'object' ? raw : null;
  }, [controlApiProbe?.lastRebindResult]);
  const rebindActions = useMemo(
    () => (Array.isArray(rebindResult?.actions) ? rebindResult.actions : []),
    [rebindResult]
  );
  const rebindDeploymentStatus = String(
    rebindResult?.deploymentStatus?.deploymentStatus || ''
  ).trim();
  const canTriggerDomainRebind = useMemo(
    () =>
      String(bootstrapFailureMeta?.nextAction || '').trim() === 'rebind_domain' &&
      Boolean(String(controlApiProbe?.taskId || '').trim()),
    [bootstrapFailureMeta?.nextAction, controlApiProbe?.taskId]
  );

  const stageList = useMemo(
    () => [
      { key: 'discover', ...provisionStages.discover },
      { key: 'probe', ...provisionStages.probe },
      { key: 'finalize', ...provisionStages.finalize },
    ],
    [provisionStages]
  );

  const updateStage = (key, status, detail = '') => {
    setProvisionStages((current) => ({
      ...current,
      [key]: { status, detail },
    }));
  };

  const resetStages = () => {
    setProvisionStages(DEFAULT_STAGE_STATE);
  };

  const pollBootstrapTaskUntilTerminal = async (taskId, normalizedToken) => {
    let lastPayload = null;
    for (let attempt = 0; attempt < 30; attempt += 1) {
      const payload = await bootstrapApi.getTask(taskId, normalizedToken);
      lastPayload = payload;
      const phaseLabel = getBootstrapPhaseLabel(payload?.phase || '');
      const progress = Number(payload?.progress || 0);
      updateStage('discover', 'running', `Task running: ${phaseLabel || 'unknown'} (${progress}%)`);

      if (BOOTSTRAP_TERMINAL_STATES.has(String(payload?.state || '').trim())) {
        return payload;
      }
      await wait(1500);
    }
    return lastPayload;
  };

  const finalizeSuccessfulProbe = (probe, attemptCount = 0, succeededAttempts = 0) => {
    updateStage(
      'probe',
      'ok',
      `Validation passed (candidates: ${attemptCount}, successful: ${succeededAttempts || 1})`
    );

    const mergedVerification = {
      ok: true,
      accountId: probe.accountId || verification?.accountId || '',
      accountName: probe.accountName || verification?.accountName || '',
      tokenStatus: probe.tokenStatus || verification?.tokenStatus || '',
      verifiedAt: new Date().toISOString(),
    };
    setVerification(mergedVerification);

    updateStage('finalize', 'running', 'Saving config (bootstrap token will be cleared from this page)');
    const bootstrapPayload = {
      cloudflareApiToken: '',
      accountId: mergedVerification.accountId,
      accountName: mergedVerification.accountName,
      tokenStatus: mergedVerification.tokenStatus,
      tokenVerifiedAt: mergedVerification.verifiedAt,
      tokenProfile: 'bootstrap',
      tokenProfileUpdatedAt: new Date().toISOString(),
      permissionAudit: probe.permissionAudit || null,
      downgradeRecommendation: probe.downgradeRecommendation || null,
      controlApiBase: probe.controlApiBase,
      routeCount: probe.routeCount || 0,
      permissionChecks: probe.checks || [],
      missingPermissions: probe.missing || [],
      shouldDeployTemplate,
      domainCheckCompleted: false,
      domainCheckResult: null,
      onboardingCompleted: false,
      provisionCompleted: true,
      controlApiBaseLocked: true,
    };

    if (probe?.resources?.d1?.id) {
      bootstrapPayload.databaseId = probe.resources.d1.id;
    }
    if (probe?.resources?.kvConfig?.id) {
      bootstrapPayload.kvConfigNamespaceId = probe.resources.kvConfig.id;
    }
    if (probe?.resources?.kvCache?.id) {
      bootstrapPayload.kvCacheNamespaceId = probe.resources.kvCache.id;
    }
    if (probe?.workerScriptName) {
      bootstrapPayload.workerScriptName = probe.workerScriptName;
    }

    saveBootstrapConfig(bootstrapPayload);
    updateStage('finalize', 'ok', 'Saved');
    setToken('');
    setControlApiProbe(null);
    resetStages();
    setControlApiError('');
    setStep(3);
  };

  const handleRetryBootstrapTask = async (forcePhase = '') => {
    const normalizedToken = token.trim();
    const taskId = String(controlApiProbe?.taskId || '').trim();
    if (!normalizedToken || !taskId) return;

    setIsRetryingTask(true);
    setControlApiError('');
    updateStage('discover', 'running', `Retrying task ${taskId}`);
    try {
      const retried = await bootstrapApi.retryTask(taskId, {
        token: normalizedToken,
        fromPhase: forcePhase || retryFromPhase || '',
      });
      const terminal = BOOTSTRAP_TERMINAL_STATES.has(String(retried?.state || '').trim())
        ? retried
        : await pollBootstrapTaskUntilTerminal(retried.taskId || taskId, normalizedToken);
      const finalProbe = terminal && typeof terminal === 'object' ? terminal : retried;
      setControlApiProbe((current) => {
        const preservedRebind =
          current?.lastRebindResult && typeof current.lastRebindResult === 'object'
            ? current.lastRebindResult
            : null;
        if (preservedRebind) {
          return {
            ...(finalProbe && typeof finalProbe === 'object' ? finalProbe : {}),
            lastRebindResult: preservedRebind,
          };
        }
        return finalProbe;
      });
      if (finalProbe?.ok) {
        const attempts = Array.isArray(finalProbe?.attempts) ? finalProbe.attempts.length : 0;
        const okAttempts = Array.isArray(finalProbe?.attempts)
          ? finalProbe.attempts.filter((item) => item.ok).length
          : 0;
        finalizeSuccessfulProbe(finalProbe, attempts, okAttempts);
      } else {
        const hint = getBootstrapNextActionHint(finalProbe?.nextAction);
        setControlApiError(
          formatBootstrapErrorMessage(finalProbe, 'Retry failed') + (hint ? ` ${hint}` : '')
        );
        updateStage('probe', 'error', 'Retry result is still blocked');
      }
    } catch (error) {
      updateStage('probe', 'error', 'Retry request failed');
      setControlApiError(formatBootstrapErrorMessage(error, 'Retry request failed'));
    } finally {
      setIsRetryingTask(false);
    }
  };

  const resolveRebindPayload = () => {
    const probeContext =
      controlApiProbe?.rebindContext && typeof controlApiProbe.rebindContext === 'object'
        ? controlApiProbe.rebindContext
        : {};
    const persistedDomain = String(persistedConfig?.domainCheckResult?.selectedDomain || '').trim();
    const persistedZoneId = String(
      persistedConfig?.domainCheckResult?.selectedZoneId || persistedConfig?.zoneId || ''
    ).trim();

    let domain = String(probeContext?.domain || '').trim();
    let zoneId = String(probeContext?.zoneId || '').trim();
    const scriptName = String(
      probeContext?.scriptName || controlApiProbe?.workerScriptName || persistedConfig?.workerScriptName || ''
    ).trim();

    if (!domain && isSafeControlDomainCandidate(persistedDomain)) {
      domain = persistedDomain;
    }
    if (!zoneId && domain === persistedDomain) {
      zoneId = persistedZoneId;
    }

    if (!domain) {
      return { ok: false, error: '无法自动确定 control 域名。请先在 Domain Scan 选择并绑定 API 子域名（如 api.example.com）。' };
    }
    if (!isSafeControlDomainCandidate(domain)) {
      return { ok: false, error: `为避免误绑主站，自动重绑仅允许 api.* 子域名（当前: ${domain}）。请在 Domain Scan 调整后重试。` };
    }
    if (!scriptName) {
      return { ok: false, error: '缺少 worker scriptName，无法执行域名重绑。请先完成 bootstrap 资源探测。' };
    }

    return { ok: true, domain, zoneId, scriptName };
  };

  const handleRebindDomainAndRetry = async () => {
    const normalizedToken = token.trim();
    const taskId = String(controlApiProbe?.taskId || '').trim();
    if (!normalizedToken || !taskId) return;

    const payload = resolveRebindPayload();
    if (!payload.ok) {
      setControlApiError(payload.error || 'Domain rebind payload is invalid');
      return;
    }

    setIsRebindingDomain(true);
    setControlApiError('');
    updateStage('discover', 'running', `Rebinding control domain: ${payload.domain}`);
    try {
      const rebindResult = await bootstrapApi.rebindDomain({
        token: normalizedToken,
        domain: payload.domain,
        zoneId: payload.zoneId,
        scriptName: payload.scriptName,
        idempotencyKey: `onboarding_rebind_${Date.now()}`,
      });
      if (rebindResult && typeof rebindResult === 'object') {
        setControlApiProbe((current) => ({
          ...(current && typeof current === 'object' ? current : {}),
          lastRebindResult: rebindResult,
        }));
      }
      await handleRetryBootstrapTask('ensure_worker');
    } catch (error) {
      if (error?.raw && typeof error.raw === 'object') {
        setControlApiProbe((current) => ({
          ...(current && typeof current === 'object' ? current : {}),
          lastRebindResult: {
            ...error.raw,
            ok: false,
          },
        }));
      }
      updateStage('probe', 'error', 'Domain rebind failed');
      setControlApiError(formatBootstrapErrorMessage(error, 'Domain rebind failed'));
    } finally {
      setIsRebindingDomain(false);
    }
  };

  const handleVerifyToken = async () => {
    const normalized = token.trim();
    if (!normalized || normalized.length < 20) {
      setTokenError('请输入有效的 Cloudflare API Token（长度不足）');
      return;
    }

    setTokenError('');
    setIsVerifyingToken(true);

    const result = await verifyCloudflareTokenAndAccount(normalized);
    setIsVerifyingToken(false);

    if (!result.ok) {
      setVerification(null);
      setTokenError(result.error || 'Token 校验失败');
      return;
    }

    setToken(normalized);
    setVerification(result);
    setControlApiProbe(null);
    setControlApiError('');
    resetStages();
    setStep(2);
  };

  const handleComplete = async () => {
    const normalizedToken = token.trim();
    const normalizedControlApiBase = normalizeControlApiBase(manualControlApiBase);
    if (!normalizedToken) {
      setTokenError('请先填写 Cloudflare API Token');
      setStep(1);
      return;
    }
    if (manualControlApiBase.trim() && !normalizedControlApiBase) {
      setControlApiError('请输入有效的 Control API 地址，例如 https://xxx.workers.dev/api');
      return;
    }

    setControlApiError('');
    setIsVerifyingControlApi(true);
    resetStages();
    updateStage(
      'discover',
      'running',
      normalizedControlApiBase
        ? `Probing manual address: ${normalizedControlApiBase}`
        : 'Starting platform auto-bootstrap (token will be sent once for provisioning)'
    );

    let probe = null;
    if (normalizedControlApiBase) {
      probe = await verifyUserControlEndpoint({
        cloudflareApiToken: normalizedToken,
        controlApiBase: normalizedControlApiBase,
        autoDetect: false,
      });
    } else {
      let provisionPayload = null;
      let provisionError = '';
      try {
        provisionPayload = await bootstrapApi.provision(normalizedToken, {
          idempotencyKey: `onboarding_${Date.now()}`,
        });
        if (provisionPayload?.taskId && String(provisionPayload?.state || '').trim() === 'running') {
          updateStage('discover', 'running', `Bootstrap task accepted: ${provisionPayload.taskId}`);
          const taskResult = await pollBootstrapTaskUntilTerminal(
            String(provisionPayload.taskId || ''),
            normalizedToken
          );
          if (taskResult) {
            provisionPayload = taskResult;
          }
        }
      } catch (error) {
        provisionError = formatBootstrapErrorMessage(error, '平台开通请求失败');
        updateStage('discover', 'error', `Platform provisioning request failed: ${provisionError}`);
      }

      const resolvedProvisionBase = normalizeControlApiBase(
        String(provisionPayload?.controlApiBase || provisionPayload?.autoControlApiBase || '')
      );

      if (provisionPayload?.ok && provisionPayload?.controlApiBase) {
        updateStage('discover', 'ok', `Connected: ${provisionPayload.controlApiBase}`);
        updateStage('probe', 'ok', 'Server-side checks passed');
        probe = {
          ...provisionPayload,
          ok: true,
          controlApiBase: provisionPayload.controlApiBase,
          checks: Array.isArray(provisionPayload.checks) ? provisionPayload.checks : [],
          missing: Array.isArray(provisionPayload.missing) ? provisionPayload.missing : [],
          error: '',
          attempts: provisionPayload.attempts || [],
          actions: provisionPayload.actions || [],
          resources: provisionPayload.resources || null,
          provisioningSteps: provisionPayload.provisioningSteps || [],
          resourceReady: provisionPayload.resourceReady,
          resourceMissing: provisionPayload.resourceMissing || [],
          permissionAudit: provisionPayload.permissionAudit || null,
          downgradeRecommendation: provisionPayload.downgradeRecommendation || null,
        };
      } else if (resolvedProvisionBase) {
        updateStage(
          'discover',
          'running',
          `Control API discovered. Running browser-side D1 / KV checks: ${resolvedProvisionBase}`
        );
        const clientProbe = await verifyUserControlEndpoint({
          cloudflareApiToken: normalizedToken,
          controlApiBase: resolvedProvisionBase,
          autoDetect: false,
        });
        probe = {
          ...(provisionPayload || {}),
          ...clientProbe,
          controlApiBase: clientProbe.controlApiBase || resolvedProvisionBase,
          provisioningSteps: provisionPayload?.provisioningSteps,
          resources: clientProbe.resources || provisionPayload?.resources,
          resourceReady: provisionPayload?.resourceReady,
          resourceMissing: provisionPayload?.resourceMissing,
          attempts: clientProbe.attempts || provisionPayload?.attempts,
          actions: clientProbe.actions || provisionPayload?.actions,
          permissionAudit: provisionPayload?.permissionAudit || null,
          downgradeRecommendation: provisionPayload?.downgradeRecommendation || null,
        };
      } else if (provisionPayload && !provisionPayload.ok) {
        const nextActionHint = getBootstrapNextActionHint(provisionPayload?.nextAction);
        probe = {
          ...provisionPayload,
          error:
            formatBootstrapErrorMessage(provisionPayload, 'Auto bootstrap blocked') +
            (nextActionHint ? ` ${nextActionHint}` : ''),
        };
      } else if (provisionError) {
        const platformBase = getPlatformApiBase();
        probe = {
          ok: false,
          checks: [],
          missing: ['control_api_base'],
          error: `Auto bootstrap failed: ${provisionError}. Confirm platform API reachability (${platformBase}) or provide Control API manually and retry.`,
          attempts: [],
          actions: [],
        };
      } else {
        probe = await verifyUserControlEndpoint({
          cloudflareApiToken: normalizedToken,
          autoDetect: true,
        });
      }
    }

    setIsVerifyingControlApi(false);
    setControlApiProbe(probe);

    const succeededAttempts = Array.isArray(probe?.attempts)
      ? probe.attempts.filter((item) => item.ok).length
      : 0;
    const attemptCount = Array.isArray(probe?.attempts) ? probe.attempts.length : 0;

    if (probe.controlApiBase) {
      updateStage('discover', 'ok', `Connected: ${probe.controlApiBase}`);
    } else if (attemptCount > 0) {
      updateStage('discover', 'error', `Tried ${attemptCount} candidate addresses, none reachable`);
    } else {
      updateStage('discover', 'error', 'No reachable Control API address found');
    }

    if (!probe.ok) {
      updateStage('probe', 'error', 'Probe failed');
      updateStage('finalize', 'idle', 'Waiting for probe to pass');
      const missingText =
        Array.isArray(probe.missing) && probe.missing.length
          ? `(missing: ${probe.missing.join(', ')})`
          : '';
      setControlApiError((probe.error || 'Auto-detection failed') + missingText);
      return;
    }

    finalizeSuccessfulProbe(probe, attemptCount, succeededAttempts);
  };

  const handleSaveLocalToken = async () => {
    const normalized = localToken.trim();
    if (!normalized || normalized.length < 20) {
      setLocalTokenError('请输入有效的 Cloudflare API Token（长度不足）');
      return;
    }
    setLocalTokenError('');
    setIsSavingLocalToken(true);
    const cfg = readBootstrapConfig();
    const base = cfg?.controlApiBase;
    if (!base) {
      setLocalTokenError("Control API not found. Click \"Re-bootstrap from platform\" below.");
      setIsSavingLocalToken(false);
      return;
    }

    const probe = await verifyUserControlEndpoint({
      cloudflareApiToken: normalized,
      controlApiBase: base,
      autoDetect: false,
    });
    setIsSavingLocalToken(false);

    if (!probe.ok) {
      const missingText =
        Array.isArray(probe.missing) && probe.missing.length
          ? `(missing: ${probe.missing.join(', ')})`
          : '';
      setLocalTokenError((probe.error || 'Token cannot access your Control API') + missingText);
      return;
    }

    const saved = saveBootstrapConfig({
      cloudflareApiToken: normalized,
      onboardingCompleted: true,
      provisionCompleted: true,
      controlApiBaseLocked: true,
      tokenProfile: 'runtime',
      tokenProfileUpdatedAt: new Date().toISOString(),
      downgradeRecommendation: {
        ...(cfg?.downgradeRecommendation && typeof cfg.downgradeRecommendation === 'object'
          ? cfg.downgradeRecommendation
          : {}),
        shouldRotate: false,
        targetProfile: 'runtime',
      },
      accountId: probe.accountId || cfg.accountId || '',
      accountName: probe.accountName || cfg.accountName || '',
      tokenStatus: probe.tokenStatus || cfg.tokenStatus || '',
      tokenVerifiedAt: new Date().toISOString(),
    });
    onComplete?.(saved);
  };

  const handleReimportFromPlatform = () => {
    resetPlatformProvisionForReimport();
    setToken('');
    setLocalToken('');
    setLocalTokenError('');
    setManualControlApiBase('');
    setVerification(null);
    setControlApiProbe(null);
    setControlApiError('');
    resetStages();
    setStep(1);
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center p-6">
      <div className="w-full max-w-3xl rounded-2xl border border-white/10 bg-slate-900/70 backdrop-blur p-8">
        <div className="mb-8">
          <p className="text-xs uppercase tracking-[0.25em] text-slate-400">Mirror-X Bootstrap</p>
          <h1 className="text-3xl font-semibold mt-2">首次启动配置</h1>
          <p className="text-slate-300 mt-2">
            For first-time setup, paste a Cloudflare API token. Mirror-X can auto-provision required resources and then clear the bootstrap token from this page.
          </p>
        </div>

        <div className="flex items-center gap-2 mb-8">
          <div className={`h-2 flex-1 rounded-full ${step >= 1 ? 'bg-cyan-400' : 'bg-white/10'}`} />
          <div className={`h-2 flex-1 rounded-full ${step >= 2 ? 'bg-cyan-400' : 'bg-white/10'}`} />
          <div className={`h-2 flex-1 rounded-full ${step >= 3 ? 'bg-cyan-400' : 'bg-white/10'}`} />
        </div>

        {step === 1 && (
          <div className="space-y-5 animate-fade-in">
            <div className="flex items-start gap-3 rounded-xl border border-cyan-300/30 bg-cyan-400/10 p-4">
              <Shield size={18} className="mt-0.5 text-cyan-300" />
              <p className="text-sm text-cyan-100">
                In this step, the token is only used for bootstrap and sent once to the platform to provision Cloudflare resources.
                After provisioning succeeds, the bootstrap token is cleared from this page and is not stored locally.
              </p>
            </div>

            <div>
              <label className="label text-slate-200">Cloudflare API Token</label>
              <input
                className="input bg-slate-950/60 border-white/20 text-white placeholder:text-slate-500"
                type="password"
                placeholder="请输入用户 Cloudflare API Token"
                value={token}
                onChange={(event) => setToken(event.target.value)}
                autoFocus
                disabled={isVerifyingToken}
              />
              {tokenError && <p className="text-sm text-red-300 mt-2">{tokenError}</p>}
            </div>

            <div className="flex justify-end">
              <button
                className="btn-primary inline-flex items-center gap-2 disabled:opacity-60"
                onClick={handleVerifyToken}
                disabled={isVerifyingToken}
              >
                {isVerifyingToken ? 'Verifying...' : 'Next'}
                {isVerifyingToken ? <Loader2 size={16} className="animate-spin" /> : <Cloud size={16} />}
              </button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-5 animate-fade-in">
            <div className="rounded-xl border border-emerald-300/30 bg-emerald-400/10 p-4">
              <div className="flex items-center gap-2 text-emerald-200">
                <CheckCircle2 size={18} />
                <p className="font-medium">Token format verification passed</p>
              </div>
              <p className="text-sm text-emerald-100 mt-2">Token: {maskedToken}</p>
              <p className="text-xs text-emerald-100/80 mt-1">
                For most users, click One-click bootstrap directly. You usually do not need to fill Control API manually.
                Step 3 will save a runtime token locally for daily operations. Control API is the worker endpoint and will be locked after bootstrap.
              </p>
            </div>

            <div>
              <label className="label text-slate-200">
                Control API address (optional) <span className="text-slate-400">leave empty for auto mode</span></label>
              <input
                className="input bg-slate-950/60 border-white/20 text-white placeholder:text-slate-500"
                type="text"
                placeholder="一般不用填；技术人员排错时填写，例如 https://xxx.workers.dev/api"
                value={manualControlApiBase}
                onChange={(event) => setManualControlApiBase(event.target.value)}
                disabled={isVerifyingControlApi}
              />
              <p className="text-xs text-slate-400 mt-2">
                留空即走「平台自动开通」。只有自动开通失败、且有人会部署 Worker 时，才需要填这里。
              </p>
            </div>

            {controlApiError && (
              <div className="rounded-xl border border-red-300/30 bg-red-500/10 p-4">
                <p className="text-sm text-red-200">{controlApiError}</p>
                {(bootstrapFailureMeta.phase ||
                  bootstrapFailureMeta.code ||
                  bootstrapFailureMeta.nextAction ||
                  bootstrapFailureMeta.traceId) && (
                  <div className="mt-3 rounded border border-red-300/30 bg-red-500/10 p-3 text-xs text-red-100">
                    {bootstrapFailureMeta.phase ? (
                      <p>阶段: {getBootstrapPhaseLabel(bootstrapFailureMeta.phase)}</p>
                    ) : null}
                    {bootstrapFailureMeta.code ? <p>错误码: {bootstrapFailureMeta.code}</p> : null}
                    {bootstrapFailureMeta.nextAction ? (
                      <p>下一步: {bootstrapFailureMeta.nextAction}</p>
                    ) : null}
                    {bootstrapFailureMeta.nextActionHint ? (
                      <p className="mt-1">{bootstrapFailureMeta.nextActionHint}</p>
                    ) : null}
                    {bootstrapFailureMeta.traceId ? (
                      <p className="mt-1">traceId: {bootstrapFailureMeta.traceId}</p>
                    ) : null}
                  </div>
                )}
                <div className="mt-3 space-y-1 text-xs text-red-100/90">
                  {recoveryTips.map((tip) => (
                    <p key={tip}>- {tip}</p>
                  ))}
                </div>
                {actionDocs.length > 0 ? (
                  <div className="mt-3 rounded border border-red-300/20 bg-red-500/5 p-3 text-xs text-red-100/95">
                    <p className="font-medium mb-1">Cloudflare 官方参考</p>
                    <div className="space-y-1">
                      {actionDocs.map((doc) => (
                        <p key={`${doc.url}_${doc.actionCode || 'action'}`}>
                          <a
                            className="underline decoration-dotted underline-offset-2 hover:opacity-90"
                            href={doc.url}
                            target="_blank"
                            rel="noreferrer"
                          >
                            {doc.label}
                          </a>
                          {doc.actionTitle ? ` (${doc.actionTitle})` : ''}
                        </p>
                      ))}
                    </div>
                  </div>
                ) : null}
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <button
                    className="btn-secondary text-xs"
                    onClick={handleComplete}
                    disabled={isVerifyingControlApi || isRetryingTask || isRebindingDomain}
                  >
                    重新探测
                  </button>
                  {canTriggerDomainRebind ? (
                    <button
                      className="btn-secondary text-xs"
                      onClick={handleRebindDomainAndRetry}
                      disabled={isVerifyingControlApi || isRetryingTask || isRebindingDomain}
                    >
                      {isRebindingDomain ? '重绑中...' : '重绑域名并重试'}
                    </button>
                  ) : null}
                  {String(controlApiProbe?.taskId || '').trim() ? (
                    <>
                      <button
                        className="btn-secondary text-xs"
                        onClick={() => handleRetryBootstrapTask('')}
                        disabled={isVerifyingControlApi || isRetryingTask || isRebindingDomain}
                      >
                        {isRetryingTask ? '重试中...' : '重试当前阶段'}
                      </button>
                      <select
                        className="input bg-slate-950/60 border-white/20 text-white text-xs py-1 px-2 h-9"
                        value={retryFromPhase}
                        onChange={(event) => setRetryFromPhase(event.target.value)}
                        disabled={isRetryingTask || isVerifyingControlApi || isRebindingDomain}
                      >
                        {RETRY_PHASE_OPTIONS.map((item) => (
                          <option key={item.value || 'current'} value={item.value}>
                            {item.label}
                          </option>
                        ))}
                      </select>
                      <button
                        className="btn-secondary text-xs"
                        onClick={() => handleRetryBootstrapTask(retryFromPhase)}
                        disabled={isVerifyingControlApi || isRetryingTask || isRebindingDomain}
                      >
                        从指定阶段重试
                      </button>
                    </>
                  ) : null}
                </div>
              </div>
            )}

            <div className="rounded-xl border border-white/10 bg-slate-950/40 p-4">
              <p className="text-sm text-slate-200 mb-3">Bootstrap progress</p>
              <div className="space-y-2">
                {stageList.map((stage) => {
                  const isRunning = stage.status === 'running';
                  const isOk = stage.status === 'ok';
                  const isError = stage.status === 'error';
                  return (
                    <div
                      key={stage.key}
                      className={`flex items-center justify-between rounded px-3 py-2 text-sm ${
                        isOk
                          ? 'bg-emerald-500/10 text-emerald-200'
                          : isError
                            ? 'bg-red-500/10 text-red-200'
                            : 'bg-slate-800/70 text-slate-200'
                      }`}
                    >
                      <span>{STAGE_LABELS[stage.key]}</span>
                      <span className="inline-flex items-center gap-2 text-xs">
                        {isRunning ? <Loader2 size={14} className="animate-spin" /> : null}
                        {isOk ? <CheckCircle2 size={14} /> : null}
                        {isError ? <XCircle size={14} /> : null}
                        {stage.detail || (stage.status === 'idle' ? '等待执行' : stage.status)}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            {Array.isArray(controlApiProbe?.attempts) && controlApiProbe.attempts.length > 0 && (
              <div className="rounded-xl border border-white/10 bg-slate-950/40 p-4">
                <p className="text-sm text-slate-200 mb-3">自动探测结果</p>
                <div className="space-y-2">
                  {controlApiProbe.attempts.map((item) => (
                    <div
                      key={item.controlApiBase}
                      className={`flex items-center justify-between rounded px-3 py-2 text-sm ${
                        item.ok ? 'bg-emerald-500/10 text-emerald-200' : 'bg-red-500/10 text-red-200'
                      }`}
                    >
                      <span className="truncate pr-3">{item.controlApiBase}</span>
                      <span className="inline-flex items-center gap-1">
                        {item.ok ? <CheckCircle2 size={14} /> : <XCircle size={14} />}
                        {item.ok ? 'ok' : item.error || 'failed'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {rebindResult && (
              <div className="rounded-xl border border-white/10 bg-slate-950/40 p-4">
                <div className="flex items-center justify-between gap-3 mb-3">
                  <p className="text-sm text-slate-200">Domain rebind details</p>
                  <span
                    className={`text-xs px-2 py-1 rounded ${getRebindStatusClass(
                      rebindResult?.ok ? 'ok' : rebindDeploymentStatus || 'pending'
                    )}`}
                  >
                    {rebindResult?.ok ? 'ready' : getRebindStatusLabel(rebindDeploymentStatus)}
                  </span>
                </div>

                <div className="grid md:grid-cols-2 gap-2 text-xs text-slate-200 mb-3">
                  <div className="rounded border border-white/10 bg-slate-900/60 p-3">
                    <p className="text-slate-400 mb-1">Domain</p>
                    <p className="break-words">{String(rebindResult?.domain || '--')}</p>
                  </div>
                  <div className="rounded border border-white/10 bg-slate-900/60 p-3">
                    <p className="text-slate-400 mb-1">Zone</p>
                    <p className="break-words">
                      {String(rebindResult?.zoneName || '--')}
                      {rebindResult?.zoneId ? ` (${rebindResult.zoneId})` : ''}
                    </p>
                  </div>
                  <div className="rounded border border-white/10 bg-slate-900/60 p-3">
                    <p className="text-slate-400 mb-1">Worker Script</p>
                    <p className="break-words">
                      {String(
                        rebindResult?.customDomainBinding?.service ||
                          rebindResult?.workerRouteBinding?.scriptName ||
                          rebindResult?.rebindContext?.scriptName ||
                          controlApiProbe?.workerScriptName ||
                          '--'
                      )}
                    </p>
                  </div>
                  <div className="rounded border border-white/10 bg-slate-900/60 p-3">
                    <p className="text-slate-400 mb-1">Route Pattern</p>
                    <p className="break-words">{String(rebindResult?.routePattern || '--')}</p>
                  </div>
                </div>

                {rebindResult?.deploymentStatus && (
                  <div className="rounded border border-white/10 bg-slate-900/60 p-3 text-xs text-slate-200 mb-3">
                    <p className="text-slate-400 mb-1">Deployment probe</p>
                    <p>
                      status: {getRebindStatusLabel(rebindResult?.deploymentStatus?.deploymentStatus || '')}
                      {rebindResult?.deploymentStatus?.statusCode
                        ? `, HTTP ${rebindResult.deploymentStatus.statusCode}`
                        : ''}
                    </p>
                    {rebindResult?.deploymentStatus?.checkedUrl ? (
                      <p className="break-all mt-1">{rebindResult.deploymentStatus.checkedUrl}</p>
                    ) : null}
                  </div>
                )}

                {rebindResult?.usedRouteFallback ? (
                  <div className="rounded border border-amber-300/30 bg-amber-500/10 p-3 text-xs text-amber-100 mb-3">
                    Custom Domain API fallback applied: route binding was used due to DNS record constraints.
                  </div>
                ) : null}

                {Array.isArray(rebindActions) && rebindActions.length > 0 ? (
                  <div className="space-y-2">
                    {rebindActions.map((item, index) => {
                      const actionStatus = String(item?.status || '').trim().toLowerCase();
                      return (
                        <div
                          key={`${item?.key || 'action'}_${index}`}
                          className={`rounded px-3 py-2 text-xs ${getRebindStatusClass(actionStatus || 'pending')}`}
                        >
                          <div className="flex items-center justify-between gap-3">
                            <span>{String(item?.title || item?.key || 'action')}</span>
                            <span>{actionStatus || 'pending'}</span>
                          </div>
                          {item?.details && typeof item.details === 'object' ? (
                            <p className="mt-1 break-words opacity-90">
                              {Object.entries(item.details)
                                .map(([k, v]) => `${k}=${String(v ?? '')}`)
                                .join(', ')}
                            </p>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                ) : null}
              </div>
            )}

            {Array.isArray(controlApiProbe?.provisioningSteps) &&
              controlApiProbe.provisioningSteps.length > 0 && (
                <div className="rounded-xl border border-white/10 bg-slate-950/40 p-4">
                  <p className="text-sm text-slate-200 mb-3">Cloudflare resource provisioning</p>
                  <div className="space-y-2">
                    {controlApiProbe.provisioningSteps.map((item) => {
                      const status = String(item?.status || '').toLowerCase();
                      const successLike = status === 'created' || status === 'existing';
                      const errorLike = status === 'error';
                      return (
                        <div
                          key={item.key}
                          className={`rounded px-3 py-2 text-sm ${
                            successLike
                              ? 'bg-emerald-500/10 text-emerald-200'
                              : errorLike
                                ? 'bg-red-500/10 text-red-200'
                                : 'bg-amber-500/10 text-amber-200'
                          }`}
                        >
                          <div className="flex items-center justify-between gap-3">
                            <span>{item.title}</span>
                            <span className="inline-flex items-center gap-1">
                              {successLike ? <CheckCircle2 size={14} /> : <XCircle size={14} />}
                              {status || 'unknown'}
                            </span>
                          </div>
                          {item.name ? <p className="text-xs mt-1 opacity-90">{item.name}</p> : null}
                          {item.message ? (
                            <p className="text-xs mt-1 opacity-90">{item.message}</p>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

            {Array.isArray(controlApiProbe?.checks) && controlApiProbe.checks.length > 0 && (
              <div className="rounded-xl border border-white/10 bg-slate-950/40 p-4">
                <p className="text-sm text-slate-200 mb-3">权限探测结果</p>
                <div className="space-y-2">
                  {controlApiProbe.checks.map((item) => (
                    <div
                      key={item.key}
                      className={`flex items-center justify-between rounded px-3 py-2 text-sm ${
                        item.ok ? 'bg-emerald-500/10 text-emerald-200' : 'bg-red-500/10 text-red-200'
                      }`}
                    >
                      <span>{item.label}</span>
                      <span className="inline-flex items-center gap-1">
                        {item.ok ? <CheckCircle2 size={14} /> : <XCircle size={14} />}
                        {item.ok ? 'ok' : item.message}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {permissionAudit && (
              <div className="rounded-xl border border-white/10 bg-slate-950/40 p-4">
                <div className="flex items-center justify-between gap-3 mb-3">
                  <p className="text-sm text-slate-200">Bootstrap permission audit</p>
                  <span
                    className={`text-xs px-2 py-1 rounded ${
                      permissionAudit.canProceed
                        ? 'bg-emerald-500/20 text-emerald-200'
                        : 'bg-red-500/20 text-red-200'
                    }`}
                  >
                    {permissionAudit.canProceed ? 'ready' : 'insufficient'}
                  </span>
                </div>
                {permissionAudit.message ? (
                  <p className="text-xs text-slate-300 mb-3">{permissionAudit.message}</p>
                ) : null}
                <div className="grid md:grid-cols-2 gap-2 text-xs">
                  <div className="rounded border border-emerald-400/30 bg-emerald-500/10 p-3">
                    <p className="text-emerald-200 mb-1">Granted scopes</p>
                    {permissionAudit.grantedScopes.length ? (
                      <p className="text-emerald-100 break-words">
                        {permissionAudit.grantedScopes.join(', ')}
                      </p>
                    ) : (
                      <p className="text-emerald-100/80">None</p>
                    )}
                  </div>
                  <div className="rounded border border-red-400/30 bg-red-500/10 p-3">
                    <p className="text-red-200 mb-1">Missing scopes</p>
                    {permissionAudit.missingScopes.length ? (
                      <p className="text-red-100 break-words">
                        {permissionAudit.missingScopes.join(', ')}
                      </p>
                    ) : (
                      <p className="text-red-100/80">None</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {onboardingDowngradeRecommendation?.shouldRotate && (
              <div className="rounded-xl border border-amber-300/30 bg-amber-400/10 p-4">
                <p className="text-sm font-medium text-amber-100">Runtime token recommendation</p>
                <p className="text-xs text-amber-100/90 mt-2">
                  {onboardingDowngradeRecommendation.message ||
                    'Use a smaller runtime token after bootstrap to reduce security risk.'}
                </p>
                {onboardingDowngradeRecommendation.recommendedScopes.length > 0 ? (
                  <p className="text-xs text-amber-100/90 mt-2">
                    Recommended runtime scopes:{' '}
                    {onboardingDowngradeRecommendation.recommendedScopes.join(', ')}
                  </p>
                ) : null}
                {onboardingDowngradeRecommendation.deadlineHours > 0 ? (
                  <p className="text-xs text-amber-100/90 mt-1">
                    Recommended rotation window:{' '}
                    {onboardingDowngradeRecommendation.deadlineHours} hours.
                  </p>
                ) : null}
              </div>
            )}
            <div>
              <p className="label text-slate-200">Deploy template first?</p>
              <div className="grid md:grid-cols-2 gap-4">
                <button
                  className={`text-left rounded-xl border p-4 transition-colors ${
                    shouldDeployTemplate
                      ? 'border-cyan-400 bg-cyan-400/10'
                      : 'border-white/10 hover:border-white/30'
                  }`}
                  onClick={() => setShouldDeployTemplate(true)}
                >
                  <p className="font-medium">Yes</p>
                  <p className="text-sm text-slate-300 mt-1">Show template deployment guidance after onboarding.</p>
                </button>
                <button
                  className={`text-left rounded-xl border p-4 transition-colors ${
                    !shouldDeployTemplate
                      ? 'border-cyan-400 bg-cyan-400/10'
                      : 'border-white/10 hover:border-white/30'
                  }`}
                  onClick={() => setShouldDeployTemplate(false)}
                >
                  <p className="font-medium">Not now</p>
                  <p className="text-sm text-slate-300 mt-1">Skip template deployment and continue to operations.</p>
                </button>
              </div>
            </div>

            <div className="flex justify-between">
              <button className="btn-secondary" onClick={() => setStep(1)}>
                Back
              </button>
              <button className="btn-primary inline-flex items-center gap-2 disabled:opacity-60" onClick={handleComplete} disabled={isVerifyingControlApi}>



                {isVerifyingControlApi ? 'Bootstrapping...' : 'One-click bootstrap (then enter local runtime token in step 3)'}
                {isVerifyingControlApi ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <Rocket size={16} />
                )}
              </button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-5 animate-fade-in">
            <div className="rounded-xl border border-amber-300/30 bg-amber-400/10 p-4">
              <p className="text-sm font-medium text-amber-100">Security recommendation</p>
              <p className="text-xs text-amber-100/90 mt-2">
                Bootstrap token should be short-lived. Save a runtime token below to
                complete onboarding and reduce long-term risk.
              </p>
              {runtimeScopeHint ? (
                <p className="text-xs text-amber-100/90 mt-1">
                  Recommended runtime scopes: {runtimeScopeHint}
                </p>
              ) : null}
              {runtimeDeadlineHint > 0 ? (
                <p className="text-xs text-amber-100/90 mt-1">
                  Recommended rotation window: {runtimeDeadlineHint} hours.
                </p>
              ) : null}
            </div>

            <div className="rounded-xl border border-cyan-300/30 bg-cyan-400/10 p-4">
              <div className="flex items-center gap-2 text-cyan-200">
                <CheckCircle2 size={18} />
                <p className="font-medium">平台开通已完成</p>
              </div>
              <p className="text-sm text-cyan-100 mt-2">
                Control API（已锁定，与 Cloudflare Token 不同，是您的后台网址）：
              </p>
              <p className="text-sm font-mono text-cyan-50 mt-1 break-all">
                {persistedConfig?.controlApiBase || '--'}
              </p>
              <p className="text-xs text-cyan-100/80 mt-2">
                Re-enter a Cloudflare API token below for daily posting and site operations.
                This runtime token is stored locally in your browser and is not sent to Mirror-X platform.
              </p>
            </div>

            <div>
              <label className="label text-slate-200">Runtime Cloudflare API Token (local-only storage)</label>
              <input
                className="input bg-slate-950/60 border-white/20 text-white placeholder:text-slate-500"
                type="password"
                placeholder="请粘贴与开通时相同或同账号、具备读写权限的新 Token"
                value={localToken}
                onChange={(event) => setLocalToken(event.target.value)}
                autoFocus
                disabled={isSavingLocalToken}
              />
              {localTokenError && <p className="text-sm text-red-300 mt-2">{localTokenError}</p>}
            </div>

            <div className="flex flex-wrap gap-3 justify-between items-center">
              <button
                type="button"
                className="btn-secondary text-sm"
                onClick={handleReimportFromPlatform}
                disabled={isSavingLocalToken}
              >
                Re-bootstrap from platform (token will be sent to platform again)
              </button>
              <button
                className="btn-primary inline-flex items-center gap-2 disabled:opacity-60"
                onClick={handleSaveLocalToken}
                disabled={isSavingLocalToken}
              >
                {isSavingLocalToken ? 'Verifying and saving...' : 'Save token and continue'}
                {isSavingLocalToken ? <Loader2 size={16} className="animate-spin" /> : <Rocket size={16} />}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default BootstrapOnboarding;


